import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  RECONNECT,
  connectToLobbyWs,
  disconnectFromLobbyWs,
  wsGaveUp,
} from '@/store/slices/lobbySlice';

/**
 * Owns the lobby WebSocket lifecycle at the App level.
 *
 * Lifted out of `LobbyChat` so the socket connects whenever the user is in a
 * lobby + has a token, regardless of which route they're on. This is what
 * lets `state.lobby.nowPlaying` populate before the user reaches `/watch/`
 * (e.g. for the post-join "Watch with the lobby?" prompt).
 *
 * Reconnect on transient close (1006) with exponential backoff (1s, 2s, 4s);
 * gives up after `RECONNECT.MAX_ATTEMPTS`.
 */
export function useLobbyWs(): void {
  const dispatch = useAppDispatch();
  const currentLobby = useAppSelector((s) => s.lobby.currentLobby);
  const token = useAppSelector((s) => s.auth.token);
  const wsState = useAppSelector((s) => s.lobby.wsState);
  const closedReason = useAppSelector((s) => s.lobby.closedReason);
  const disconnected = useAppSelector((s) => s.lobby.disconnected);

  const shouldConnect = currentLobby != null && token != null;
  const lobbyId = currentLobby?.id ?? null;

  const attemptsRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);

  // Connect when entering a lobby; tear down on unmount / lobby change.
  useEffect(() => {
    if (!shouldConnect || !lobbyId || !token) return;
    let cancelled = false;
    attemptsRef.current = 0;

    void dispatch(connectToLobbyWs({ lobbyId, token }));

    return () => {
      cancelled = true;
      if (reconnectTimerRef.current != null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      // Tear down via the thunk so the open/close/event subscribers installed
      // by `connectToLobbyWs` are removed — otherwise stale handlers fire on
      // the next connection and double-dispatch wsOpened/wsClosed.
      void dispatch(disconnectFromLobbyWs());
      // touch cancelled so it's not flagged unused — the close-handler chain
      // inside the slice does its own teardown; this guard is just defensive.
      void cancelled;
    };
  }, [shouldConnect, lobbyId, token, dispatch]);

  // Reconnect with exponential backoff after a transient close.
  useEffect(() => {
    if (!shouldConnect || !lobbyId || !token) return;
    if (wsState !== 'closed') return;
    if (closedReason) return; // lobby ended — don't retry
    if (disconnected) return; // already gave up
    if (attemptsRef.current >= RECONNECT.MAX_ATTEMPTS) {
      dispatch(wsGaveUp());
      return;
    }
    const backoff = RECONNECT.BACKOFF_BASE_MS * Math.pow(2, attemptsRef.current);
    attemptsRef.current += 1;
    if (reconnectTimerRef.current != null) {
      window.clearTimeout(reconnectTimerRef.current);
    }
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      void dispatch(connectToLobbyWs({ lobbyId, token }));
    }, backoff);
    return () => {
      if (reconnectTimerRef.current != null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [wsState, shouldConnect, lobbyId, token, closedReason, disconnected, dispatch]);

  // Reset attempts whenever we successfully open.
  useEffect(() => {
    if (wsState === 'open') attemptsRef.current = 0;
  }, [wsState]);
}
